import { useState } from "react";
import { Calendar, Image as ImageIcon, FileText, Zap, Layers, Ratio, Cpu, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { IconBox } from "@/components/flowly/infra/IconBox/IconBox";
import { getResizedImageUrl } from "@/lib/imageUrl";
import type { LightboxDetailsSectionProps } from "./types";
import { PROMPT_TRUNCATION_THRESHOLD, RESOLUTION_PATTERNS, DEFAULT_RESOLUTION_DISPLAY } from "./constants";

function getResolutionDisplay(res: string | undefined): string {
  if (!res) return DEFAULT_RESOLUTION_DISPLAY;
  for (const [label, patterns] of Object.entries(RESOLUTION_PATTERNS)) {
    if (patterns.some((p) => res.includes(p))) return label;
  }
  return DEFAULT_RESOLUTION_DISPLAY;
}

export function LightboxDetailsSection({
  formattedDate,
  dateLabel,
  sourceImageUrl,
  sourceImageLabel,
  prompt,
  promptLabel,
  resolution,
  resolutionLabel,
  aspectRatio,
  aspectRatioLabel,
  pipeline,
  pipelineLabel,
  model,
  modelLabel,
  duration,
  durationLabel,
  onSourceImageClick,
}: LightboxDetailsSectionProps) {
  const { t } = useLanguage();
  const [showFullPrompt, setShowFullPrompt] = useState(false);

  const isLongPrompt = prompt && prompt.length > PROMPT_TRUNCATION_THRESHOLD;

  return (
    <div className="flex flex-col gap-3">
      {/* Model */}
      {model && modelLabel && (
        <div className="flex items-center gap-2 text-sm">
          <IconBox size="sm" variant="muted" rounded="lg">
            <Cpu className="w-3.5 h-3.5 text-muted-foreground" />
          </IconBox>
          <div>
            <p className="text-muted-foreground text-xs">{modelLabel}</p>
            <p className="text-foreground font-medium text-sm">{model}</p>
          </div>
        </div>
      )}

      {/* Resolution */}
      {resolution !== undefined && resolutionLabel && (
        <div className="flex items-center gap-2 text-sm">
          <IconBox size="sm" variant="muted" rounded="lg">
            <Zap className="w-3.5 h-3.5 text-muted-foreground" />
          </IconBox>
          <div>
            <p className="text-muted-foreground text-xs">{resolutionLabel}</p>
            <p className="text-foreground font-medium text-sm">{getResolutionDisplay(resolution)}</p>
          </div>
        </div>
      )}

      {/* Aspect Ratio */}
      {aspectRatio && aspectRatioLabel && (
        <div className="flex items-center gap-2 text-sm">
          <IconBox size="sm" variant="muted" rounded="lg">
            <Ratio className="w-3.5 h-3.5 text-muted-foreground" />
          </IconBox>
          <div>
            <p className="text-muted-foreground text-xs">{aspectRatioLabel}</p>
            <p className="text-foreground font-medium text-sm">{aspectRatio}</p>
          </div>
        </div>
      )}

      {/* Duration (video only) */}
      {duration != null && durationLabel && (
        <div className="flex items-center gap-2 text-sm">
          <IconBox size="sm" variant="muted" rounded="lg">
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
          </IconBox>
          <div>
            <p className="text-muted-foreground text-xs">{durationLabel}</p>
            <p className="text-foreground font-medium text-sm">{duration}{t.lightbox.secondsShort}</p>
          </div>
        </div>
      )}

      {/* Pipeline/Creation Type */}
      {pipeline && pipelineLabel && (
        <div className="flex items-center gap-2 text-sm">
          <IconBox size="sm" variant="muted" rounded="lg">
            <Layers className="w-3.5 h-3.5 text-muted-foreground" />
          </IconBox>
          <div>
            <p className="text-muted-foreground text-xs">{pipelineLabel}</p>
            <p className="text-foreground font-medium text-sm">{pipeline}</p>
          </div>
        </div>
      )}

      {/* Creation Date */}
      <div className="flex items-center gap-2 text-sm">
        <IconBox size="sm" variant="muted" rounded="lg">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
        </IconBox>
        <div>
          <p className="text-muted-foreground text-xs">{dateLabel}</p>
          <p className="text-foreground font-medium text-sm">{formattedDate}</p>
        </div>
      </div>

      {/* Source Image - Clickable if handler provided */}
      {sourceImageUrl && (
        <div className="flex items-start gap-2">
          <IconBox size="sm" variant="muted" rounded="lg" className="flex-shrink-0">
            <ImageIcon className="w-3.5 h-3.5 text-muted-foreground" />
          </IconBox>
          <div className="flex-1">
            <p className="text-muted-foreground text-xs mb-1">
              {sourceImageLabel}
            </p>
            <div
              className={cn(
                "w-16 h-16 rounded-lg overflow-hidden border border-border",
                onSourceImageClick && "cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              )}
              onClick={onSourceImageClick}
              title={onSourceImageClick ? t.lightbox.clickToUseImage : undefined}
            >
              <img
                src={getResizedImageUrl(sourceImageUrl, 128)}
                loading="lazy"
                alt="Source"
                className="w-full h-full object-cover"
              />
            </div>
            {onSourceImageClick && (
              <p className="text-xs text-primary mt-1 font-medium">{t.lightbox.clickToUseSource}</p>
            )}
          </div>
        </div>
      )}

      {/* Prompt */}
      {prompt && promptLabel && (
        <div className="flex items-start gap-2">
          <IconBox size="sm" variant="muted" rounded="lg" className="flex-shrink-0">
            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
          </IconBox>
          <div className="flex-1">
            <p className="text-muted-foreground text-xs mb-1">{promptLabel}</p>
            <p
              className={cn(
                "text-foreground text-sm bg-secondary/50 rounded-lg p-2 border border-border",
                !showFullPrompt && isLongPrompt && "line-clamp-3"
              )}
            >
              {prompt}
            </p>
            {isLongPrompt && (
              <button
                type="button"
                onClick={() => setShowFullPrompt(!showFullPrompt)}
                className="text-xs text-primary mt-1 font-medium hover:underline"
              >
                {showFullPrompt ? t.lightbox.showLess : t.lightbox.showMore}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
